import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Avatar,
    Chip,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    InputAdornment,
    IconButton,
    Paper,
    Tooltip,
    CircularProgress,
    Container,
    Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { GridView as GridViewIcon, ViewList as ViewListIcon } from "@mui/icons-material";
import CustomPagination from "../../components/common/CustomPagination";
import CardPagination from "../../components/common/CardPagination";
import { Visibility, VisibilityOff, EmailOutlined, PersonOutlined, EditOutlined } from "@mui/icons-material";
import CustomInput from "../../components/common/CustomInput";
import CustomFilter from "../../components/common/CustomFilter";
import CustomButton from "../../components/common/CustomButton";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomHeader from "../../components/common/CustomHeader";
import CustomTable from "../../components/common/CustomTable";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { ERROR_MESSAGES, getPlanExhaustMessage } from "../../components/common/StatusDictionary";
import { userApi } from "../../api/userApi";
import AddNewUserSchema from "../../components/validator/Admin/AddNewUser";

export const userFilter = [
    {
        key: "status",
        label: "Status",
        type: "checkbox",
        options: [
            { label: "Active", value: "active" },
            { label: "In Active", value: "inactive" },
        ],
    }
];

/* ── Status Chips ───────────────────────────────────────── */

const STATUS_CHIP = {
    active: (
        <Chip
            label="Active"
            size="small"
            sx={{
                fontWeight: 600,
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                color: 'success.main',
                borderRadius: '6px'
            }}
        />
    ),
    inactive: (
        <Chip
            label="Inactive"
            size="small"
            sx={{
                fontWeight: 600,
                bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.1),
                color: 'text.secondary',
                borderRadius: '6px'
            }}
        />
    ),
};

const RecruiterCard = ({ user, theme }) => {
    // Generate avatar background color based on name length or email
    const nameSeed = user.full_name || user.email || "";
    const colors = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.info.main,
        theme.palette.warning.main,
        theme.palette.success.main,
    ];
    const colorIndex = nameSeed.length % colors.length;
    const avatarBg = colors[colorIndex];

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                bgcolor: "background.paper",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                transition: "all 0.2s ease-in-out",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                boxShadow: theme.palette.mode === "dark" ? "none" : "0 2px 8px rgba(0,0,0,0.02)",
                "&:hover": {
                    borderColor: theme.palette.primary.main,
                    transform: "translateY(-3px)",
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                },
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
                <Avatar
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: avatarBg,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        boxShadow: `0 4px 12px ${alpha(avatarBg, 0.3)}`
                    }}
                >
                    {nameSeed.slice(0, 2).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        noWrap
                        sx={{ color: "text.primary", lineHeight: 1.2, mb: 0.5 }}
                    >
                        {user.full_name || "Unknown User"}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: "block", fontWeight: 500 }}
                    >
                        {user.email}
                    </Typography>
                </Box>
            </Stack>

            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                {STATUS_CHIP[user.status?.toLowerCase()] ?? <Chip label={user.status} size="small" />}
            </Box>

            <Box
                sx={{
                    mt: "auto",
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                        Job Descriptions
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                        {user.jd_count}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                        Tokens Used
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                        {Number(user.total_tokens_used || 0).toLocaleString()}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default function UserManagementPage() {
    const theme = useTheme();
    const [viewMode, setViewMode] = useState("list"); // "list" | "grid"
    const [cardUsers, setCardUsers] = useState([]);
    const [cardPage, setCardPage] = useState(1);
    const [cardHasMore, setCardHasMore] = useState(true);
    const [cardLoading, setCardLoading] = useState(false);
    const allCardUsersRef = useRef([]);

    const [users, setUsers] = useState([]);
    const allUsersRef = useRef([]);
    const [openForm, setOpenForm] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [editLoading, setEditLoading] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const [formData, setFormData] = useState({
        email: "",
        user_type: "",
    });

    const [formErrors, setFormErrors] = useState({});
    const [tableLoading, setTableLoading] = useState(true);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState([]);
    const [searchValue, setSearchValue] = useState("");

    const [editOpen, setEditOpen] = useState(false);

    const [editUser, setEditUser] = useState({
        id: "",
        email: "",
    });

    /* ── Component ─────────────────────────────────────────── */
    const handleEdit = (row) => {
        setEditUser({
            id: row.id,
            email: row.email,
        });
        setEditOpen(true);
    };

    const handleUpdateEmail = async () => {
        const normalizedEmail = editUser.email.trim().toLowerCase();
        setEditUser(prev => ({ ...prev, email: normalizedEmail }));

        if (!normalizedEmail) {
            setSnackbar({
                open: true,
                severity: "warning",
                message: "Email is required",
            });
            return;
        }

        try {
            setEditLoading(true);
            const response = await userApi.updateRecruiterEmail({
                user_id: editUser.id,
                email: normalizedEmail,
            });

            if (response.status === 200) {
                setSnackbar({
                    open: true,
                    severity: "success",
                    message: response.message,
                });
                setEditOpen(false);

                // Refresh table
                if (viewMode === "list") {
                    fetchData(page + 1, false, false);
                } else {
                    fetchData(1, true, false);
                }
            } else {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: response.message,
                });
            }
        } finally {
            setEditLoading(false);
        }
    };

    const buildFilterPayload = (filters) => {
        const grouped = {};

        filters.forEach(({ property, value, operator }) => {
            const actualValue = value?.value ?? value;

            if (!grouped[property]) {
                grouped[property] = {
                    property,
                    value: [],
                    ...(operator && { operator }),
                };
            }
            grouped[property].value.push(actualValue);
        });

        return Object.values(grouped).map((item) => ({
            ...item,
            value: item.value.length === 1 ? item.value[0] : item.value,
        }));
    };

    const handleFilterChange = (key, value, mode, operator = "equal") => {
        setFilters((prev) => {
            if (mode === "single") {
                const filtered = prev.filter((f) => f.property !== key);
                return [
                    ...filtered,
                    {
                        property: key,
                        value,
                        operator,
                    },
                ];
            }

            const exists = prev.some(
                (f) => f.property === key && f.value === value
            );

            if (exists) {
                return prev.filter(
                    (f) => !(f.property === key && f.value === value)
                );
            }

            return [
                ...prev,
                {
                    property: key,
                    value,
                },
            ];
        });
    };

    /* ── Fetch Data (Unified) ─────────────────────────────── */

    const fetchData = useCallback(async (targetPage = 1, isCard = false, shouldAppend = false) => {
        try {
            if (isCard) setCardLoading(true);
            else setTableLoading(true);

            const isFiltering = filters.length > 0;
            const size = isCard ? 20 : rowsPerPage;
            let response;

            if (isFiltering) {
                const payload = buildFilterPayload(filters);
                response = await userApi.filterListRecruiter({
                    "filters": payload,
                    page: targetPage,
                    page_size: size
                });
            } else {
                response = await userApi.listRecruiter({
                    page: targetPage,
                    page_size: size
                });
            }

            const { status, data } = response || {};

            if (status === 200 && data) {
                const results = data.results || [];
                if (isCard) {
                    setCardUsers((prev) => shouldAppend ? [...prev, ...results] : results);
                    allCardUsersRef.current = shouldAppend ? [...allCardUsersRef.current, ...results] : results;

                    const pagination = data?.pagination || {};
                    setCardHasMore(pagination.has_next || false);
                    setCardPage(targetPage);
                } else {
                    setUsers(results);
                    setTotal(data?.pagination?.total || 0);
                    setTotalPages(data?.pagination?.total_pages || 1);
                    allUsersRef.current = results;
                }
            } else {
                if (isCard) {
                    if (!shouldAppend) {
                        setCardUsers([]);
                        allCardUsersRef.current = [];
                    }
                    setCardHasMore(false);
                } else {
                    setUsers([]);
                    setTotal(0);
                    setTotalPages(1);
                    allUsersRef.current = [];
                }
            }
        } catch (error) {
            console.error("Error fetching recruiter data:", error);
            if (isCard) {
                if (!shouldAppend) {
                    setCardUsers([]);
                    allCardUsersRef.current = [];
                }
                setCardHasMore(false);
            } else {
                setUsers([]);
                allUsersRef.current = [];
            }
        } finally {
            if (isCard) setCardLoading(false);
            else setTableLoading(false);
        }
    }, [filters, rowsPerPage]);

    const handleApply = async () => {
        if (viewMode === "list") {
            setPage(0);
            fetchData(1, false, false);
        } else {
            fetchData(1, true, false);
        }
    };

    const handleClear = () => {
        setFilters([]);
        setSearchValue("");
        setTimeout(() => {
            if (viewMode === "list") {
                setPage(0);
                fetchData(1, false, false);
            } else {
                fetchData(1, true, false);
            }
        }, 0);
    };

    useEffect(() => {
        if (viewMode === "list") {
            fetchData(page + 1, false, false);
        }
    }, [fetchData, page, viewMode]);

    const handlePageChange = (newPage) => {
        setPage(newPage - 1);
    };

    const handlePageSizeChange = (newSize) => {
        setRowsPerPage(newSize);
        setPage(0);
    };

    const handleLoadMoreCards = () => {
        if (cardHasMore && !cardLoading) {
            fetchData(cardPage + 1, true, true);
        }
    };

    const handleViewModeChange = (newMode) => {
        if (newMode === viewMode) return;
        setViewMode(newMode);
        setSearchValue("");
        if (newMode === "list") {
            setPage(0);
        } else {
            fetchData(1, true, false);
        }
    };

    /* ── Form Handlers ───────────────────────────────────── */

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        if (name === "email") {
            processedValue = value?.trim()?.toLowerCase();
        }
        setFormData((prev) => ({ ...prev, [name]: processedValue }));

        if (!String(processedValue).trim()) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
            return;
        }

        try {
            AddNewUserSchema.validateSyncAt(name, { [name]: processedValue });
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        } catch (error) {
            setFormErrors((prev) => ({ ...prev, [name]: error.message }));
        }
    };

    const handleCreateUser = async () => {
        const normalizedEmail = formData.email.trim().toLowerCase();
        const normalizedFormData = { ...formData, email: normalizedEmail };
        setFormData(normalizedFormData);

        try {
            AddNewUserSchema.validateSync(normalizedFormData, { abortEarly: false });
            setFormErrors({});
        } catch (error) {
            const validationErrors = {};
            if (error.inner) {
                error.inner.forEach((err) => {
                    validationErrors[err.path] = err.message;
                });
            } else {
                validationErrors[error.path] = error.message;
            }
            setFormErrors(validationErrors);
            return;
        }

        try {
            setLoading(true);

            const response = await userApi.onboardRecruiter(normalizedFormData);
            const status = response?.status;
            const message = response?.message;

            if (status === 201) {
                setOpenForm(false);
                setFormData({ email: "", user_type: "" });
                setFormErrors({});

                setTimeout(() => {
                    setSnackbar({
                        open: true,
                        message: message || "Recruiter invited successfully!",
                        severity: "success",
                    });
                    if (viewMode === "list") fetchData(page + 1, false, false);
                    else fetchData(1, true, false);
                }, 250);
                return;
            }

            if (status === 207) {
                setOpenForm(false);
                setFormData({ email: "", user_type: "" });
                setFormErrors({});

                setTimeout(() => {
                    setSnackbar({
                        open: true,
                        message: message || "Recruiter created successfully but invitation email failed.",
                        severity: "warning",
                    });
                    if (viewMode === "list") fetchData(page + 1, false, false);
                    else fetchData(1, true, false);
                }, 250);
                return;
            }

            if (status === 409) {
                setSnackbar({ open: true, message: message || "User email already exists", severity: "warning" });
                return;
            }

            if (status === 500) {
                setSnackbar({ open: true, message: message || "Internal server error", severity: "error" });
                return;
            }

            setSnackbar({ open: true, message: message, severity: "error" });

        } catch (error) {
            console.error("Failed to create user:", error);
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || ERROR_MESSAGES.UnexpectedError || "An unexpected error occurred",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSearch = (value) => {
        setSearchValue(value);

        if (!value) {
            if (viewMode === "list") setUsers(allUsersRef.current);
            else setCardUsers(allCardUsersRef.current);
            return;
        }

        const lower = value.toLowerCase();

        if (viewMode === "list") {
            const filtered = allUsersRef.current.filter((item) => {
                return (
                    item.full_name?.toLowerCase().includes(lower) ||
                    item.email?.toLowerCase().includes(lower)
                );
            });
            setUsers(filtered);
        } else {
            const filtered = allCardUsersRef.current.filter((item) => {
                return (
                    item.full_name?.toLowerCase().includes(lower) ||
                    item.email?.toLowerCase().includes(lower)
                );
            });
            setCardUsers(filtered);
        }
    };

    /* ── Table Columns ───────────────────────────────────── */

    const columns = useMemo(
        () => [
            {
                label: "User Details",
                field: "full_name",
                sortable: true,
                stickyFirst: true,
                align: "left",
                headerAlign: "left",
                sx: { width: "35%" },
                render: (_, row) => (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                            sx={{
                                width: 44,
                                height: 44,
                                fontSize: "1.05rem",
                                fontWeight: 700,
                                background: "linear-gradient(135deg, #1ea7fd 0%, #7b61ff 100%)",
                                boxShadow: "0 4px 10px rgba(123, 97, 255, 0.2)"
                            }}
                        >
                            {row.full_name?.slice(0, 2).toUpperCase() || row.email?.slice(0, 2).toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "0.95rem",
                                    lineHeight: 1.2,
                                    color: "text.primary"
                                }}
                            >
                                {row.full_name || "Unknown User"}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    mt: 0.5,
                                    fontWeight: 500,
                                }}
                            >
                                {row.email}
                            </Typography>
                        </Box>
                    </Stack>
                ),
            },
            {
                label: "Job Descriptions",
                field: "jd_count",
                align: "center",
                headerAlign: "center",
                render: (value) => (
                    <Typography sx={{ fontWeight: 600, color: "text.secondary" }}>
                        {Number(value || 0).toLocaleString()}
                    </Typography>
                ),
            },
            {
                label: "Credits Used",
                field: "total_tokens_used",
                align: "center",
                headerAlign: "center",
                render: (value) => (
                    <Typography sx={{ fontWeight: 600, color: "text.secondary" }}>
                        {Number(value || 0).toLocaleString()}
                    </Typography>
                ),
            },
            {
                label: "Status",
                field: "status",
                align: "center",
                headerAlign: "center",
                render: (value) =>
                    STATUS_CHIP[value?.toLowerCase()] ?? (
                        <Chip label={value} size="small" sx={{ fontWeight: 600, borderRadius: '6px' }} />
                    ),
            },
            {
                label: "Action",
                field: "action",
                align: "center",
                headerAlign: "center",
                render: (_, row) => {
                    // Check if status is active to disable edit button
                    const isActive = row.status?.toLowerCase() === "active";

                    return (
                        <Tooltip title={isActive ? "Cannot edit an active user" : "Edit email"} placement="top">
                            {/* Span is required in MUI to show tooltips on disabled buttons */}
                            <span>
                                <CustomButton
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleEdit(row)}
                                    disabled={isActive}
                                    startIcon={<EditOutlined fontSize="small" />}
                                    sx={{
                                        borderRadius: "8px",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        borderWidth: "1px",
                                        px: 2,
                                        borderColor: isActive ? "divider" : "primary.main",
                                        "&:hover": {
                                            borderWidth: "1px",
                                            bgcolor: alpha(theme.palette.primary.main, 0.04)
                                        }
                                    }}
                                >
                                    Edit
                                </CustomButton>
                            </span>
                        </Tooltip>
                    );
                },
            },
        ],
        [theme]
    );

    /* ── UI ─────────────────────────────────────────────── */

    return (
        <Box sx={{ minHeight: "100vh" }}>
            <CustomHeader title="User Management" />

            <Container
                maxWidth="xl"
                sx={{
                    mt: 3,
                    mb: 4,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    {/* View Mode Toggle */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "10px",
                            overflow: "hidden",
                            bgcolor: "background.paper",
                            boxShadow: theme.palette.mode === "dark" ? "none" : "0 2px 6px rgba(0,0,0,0.02)",
                        }}
                    >
                        <Tooltip title="Table view" placement="bottom">
                            <IconButton
                                size="small"
                                onClick={() => handleViewModeChange("list")}
                                sx={{
                                    borderRadius: 0,
                                    height: 40,
                                    width: 44,
                                    bgcolor: viewMode === "list" ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                                    color: viewMode === "list" ? theme.palette.primary.main : theme.palette.text.secondary,
                                    "&:hover": {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                }}
                            >
                                <ViewListIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                        <Box
                            sx={{
                                width: "1px",
                                height: 24,
                                bgcolor: "divider",
                            }}
                        />
                        <Tooltip title="Card view" placement="bottom">
                            <IconButton
                                size="small"
                                onClick={() => handleViewModeChange("grid")}
                                sx={{
                                    borderRadius: 0,
                                    height: 40,
                                    width: 44,
                                    bgcolor: viewMode === "grid" ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                                    color: viewMode === "grid" ? theme.palette.primary.main : theme.palette.text.secondary,
                                    "&:hover": {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                }}
                            >
                                <GridViewIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <CustomButton
                        variant="contained"
                        onClick={() => setOpenForm(true)}
                    >
                        Add User
                    </CustomButton>
                </Box>

                {viewMode === "list" ? (
                    tableLoading ? (
                        <SkeletonLoader type="table" rows={5} columns={columns} />
                    ) : (
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: theme.palette.mode === "dark" ? "none" : "0 4px 20px rgba(0,0,0,0.03)"
                            }}
                        >
                            <CustomTable columns={columns} rows={users} />
                            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1 }}>
                                <CustomPagination
                                    page={page + 1}
                                    totalPages={totalPages}
                                    total={total}
                                    pageSize={rowsPerPage}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handlePageSizeChange}
                                    pageSizeOptions={[5, 10, 25]}
                                    sx={{ mt: 0 }}
                                />
                            </Box>
                        </Paper>
                    )
                ) : (
                    <CardPagination
                        hasMore={cardHasMore}
                        loading={cardLoading}
                        onLoadMore={handleLoadMoreCards}
                    >
                        {cardUsers.length === 0 && !cardLoading ? (
                            <Box sx={{ py: 8, textAlign: "center", width: "100%" }}>
                                <Typography variant="body1" color="text.secondary">
                                    No recruiters found.
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "repeat(2, 1fr)",
                                        md: "repeat(3, 1fr)",
                                        xl: "repeat(4, 1fr)",
                                    },
                                    gap: 3,
                                }}
                            >
                                {cardUsers.map((user) => (
                                    <RecruiterCard key={user.id} user={user} theme={theme} />
                                ))}
                            </Box>
                        )}
                    </CardPagination>
                )}
            </Container>

            <Dialog
                open={openForm}
                keepMounted={false}
                onClose={(event, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown" || loading) {
                        return;
                    }
                    setOpenForm(false);
                    setFormData({ email: "", user_type: "" });
                    setFormErrors({});
                }}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1
                    },
                }}
            >
                <DialogTitle
                    variant="h5"
                    align="center"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        pb: 0.3,
                    }}
                >
                    Add New User
                </DialogTitle>
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mb: 1,
                        fontSize: "0.85rem",
                    }}>
                    Enter the email address of the user you want to add.
                </Typography>
                <Divider />


                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleCreateUser();
                    }}
                >
                    <DialogContent>
                        <Stack spacing={2.5}>
                            <CustomInput
                                label="Email"
                                name="email"
                                size="small"
                                placeholder="Enter user email eg. example@xyz.com"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                error={!!formErrors.email}
                                helperText={formErrors.email}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailOutlined sx={{ color: "text.secondary" }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <CustomInput
                                select
                                label="User Type"
                                name="user_type"
                                size="small"
                                placeholder="Select user type"
                                value={formData.user_type}
                                onChange={handleChange}
                                required
                                error={!!formErrors.user_type}
                                helperText={formErrors.user_type}
                                SelectProps={{
                                    displayEmpty: true,
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonOutlined sx={{ color: "text.secondary" }} />
                                        </InputAdornment>
                                    ),
                                }}
                            >
                                <MenuItem value="recruiter">Recruiter</MenuItem>
                            </CustomInput>
                        </Stack>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            px: 3,
                            pb: 3,
                            display: "flex",
                            flexDirection: "row",
                            gap: 2,
                        }}
                    >
                        <CustomButton
                            variant="outlined"
                            sx={{
                                flex: 1,
                            }}
                            onClick={() => {
                                setOpenForm(false)
                                setFormData({ email: "", user_type: "" })
                                setFormErrors({})
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            type="submit"
                            variant="contained"
                            disabled={loading || !formData.email.trim() || !formData.user_type || Object.values(formErrors).some((v) => v)}
                            sx={{
                                flex: 1,
                            }}
                        >
                            {loading ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CircularProgress size={20} color="inherit" />
                                    Creating...
                                </span>
                            ) : (
                                "Create User"
                            )}
                        </CustomButton>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1
                    }
                }}
            >
                <DialogTitle
                    align="center"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,

                        // pt: 3
                    }}
                >
                    Edit Recruiter Email
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    <CustomInput
                        fullWidth
                        label="Email"
                        name="email"
                        size="small"
                        required
                        value={editUser.email}
                        onChange={(e) =>
                            setEditUser({
                                ...editUser,
                                email: e.target.value,
                            })
                        }
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2 }}>
                    <CustomButton
                        variant="outlined"
                        sx={{ flex: 1 }}
                        onClick={() => setEditOpen(false)}
                    >
                        Cancel
                    </CustomButton>
                    <CustomButton
                        variant="contained"
                        sx={{ flex: 1 }}
                        onClick={handleUpdateEmail}
                        disabled={editLoading}
                    >
                        {editLoading ? "Updating..." : "Update"}
                    </CustomButton>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </Box>
    );
}

